import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button, Tag, Card, Checkbox, Icon, Title, Text, Tabs,
} from '@nimbus-ds/components'
import {
  ClockIcon,
  CheckCircleIcon,
  StopIcon,
  ExclamationCircleIcon,
  ArchiveIcon,
  ArrowRightIcon,
  LightbulbIcon,
} from '@nimbus-ds/icons'
import { FunnelAnalytics } from '@/components/funnel/FunnelAnalytics'
import { CustomEventsSection } from '@/components/analytics/CustomEventsSection'
import { useToast } from '@/hooks/use-toast'
import type { Experiment } from '@/types'
import { experimentService } from '@/api/experiments'
import { funnelService } from '@/api/funnel'
import { authService } from '@/api/auth'

export function ResultsPage() {
  const navigate = useNavigate()
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [loading, setLoading] = useState(true)
  const [storeId, setStoreId] = useState<string | null>(null)
  const [funnelData, setFunnelData] = useState<any | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [windowDays, setWindowDays] = useState<number>(7)
  const { toast } = useToast()

  useEffect(() => {
    loadExperiments()
    const id = authService.getCurrentStoreId()
    setStoreId(id)
  }, [])

  useEffect(() => {
    if (!storeId) return
    setFunnelData(null)
    funnelService
      .getStoreFunnelAnalytics(storeId, windowDays)
      .then(setFunnelData)
      .catch((error) => console.error('Failed to load funnel data:', error))
  }, [storeId, windowDays])

  const loadExperiments = async () => {
    try {
      setLoading(true)
      const data = await experimentService.getExperiments()
      setExperiments(data)
    } catch (error) {
      console.error('Failed to load experiments:', error)
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los experimentos.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 animate-pulse rounded bg-muted"></div>
            <div className="h-4 w-64 animate-pulse rounded bg-muted"></div>
          </div>
        </div>
        <div className="h-96 animate-pulse rounded bg-muted"></div>
      </div>
    )
  }

  // Separate experiments by status for the list view
  const visibleExperiments = showArchived ? experiments : experiments.filter(e => e.status !== 'archived')
  const activeExperiments = visibleExperiments.filter(e => e.status === 'active')
  const completedExperiments = visibleExperiments.filter(e => e.status === 'completed')
  const archivedExperiments = showArchived ? experiments.filter(e => e.status === 'archived') : []
  const otherExperiments = visibleExperiments.filter(e => e.status !== 'active' && e.status !== 'completed' && e.status !== 'archived')

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Title as="h1">Resultados</Title>
          <Text>
            Analiza el rendimiento de tu tienda y experimentos
          </Text>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 self-start sm:self-auto">
          <div className="flex gap-1 rounded-md border p-0.5">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                appearance={windowDays === d ? 'primary' : 'transparent'}
                size="small"
                onClick={() => setWindowDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
          {(() => {
            const start = funnelData?.window_start
            const end = funnelData?.window_end
            if (!start || !end) return null
            const fmt = (iso: string) =>
              new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
            return (
              <Text fontSize="caption" className="tabular-nums">
                {fmt(start)} — {fmt(end)}
              </Text>
            )
          })()}
        </div>
      </div>

      {/* Results Tabs - Now 2 tabs */}
      <Tabs>
        {/* Dashboard Tab - Store-level Funnel Analytics + Custom Events */}
        <Tabs.Item label="Dashboard">
          <div className="mt-4 space-y-5">
            <FunnelAnalytics
              experiments={experiments}
              initialFunnelData={funnelData}
              windowDays={windowDays}
            />

            {/* Custom Events Section */}
            {storeId && (
              <div className="border-t pt-5">
                <Title as="h3" className="mb-4">Eventos Personalizados</Title>
                <CustomEventsSection storeId={storeId} windowDays={windowDays} funnelData={funnelData} />
              </div>
            )}
          </div>
        </Tabs.Item>

        {/* Experiments Tab - List with navigation to dedicated pages */}
        <Tabs.Item
          label="Experimentos"
          labelContent={
            activeExperiments.length > 0 ? (
              <div className="shrink-0 whitespace-nowrap">
                <Tag appearance="neutral">{activeExperiments.length}</Tag>
              </div>
            ) : undefined
          }
        >
          <div className="mt-4 space-y-5">
            {/* Include Archived toggle */}
            {experiments.some(e => e.status === 'archived') && (
              <Checkbox
                name="show-archived-results"
                label="Incluir archivados"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            )}

            {visibleExperiments.length === 0 ? (
              <Card>
                <Card.Body>
                  <div className="py-12 text-center">
                    <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-muted">
                      <LightbulbIcon size={48} />
                    </div>
                    <Title as="h3" className="mb-2">
                      No hay experimentos
                    </Title>
                    <Text>
                      Crea tu primer experimento para empezar a optimizar tu tienda
                    </Text>
                    <div className="mt-6 flex justify-center">
                      <Button appearance="primary" onClick={() => navigate('/dashboard/experiments')}>
                        Ir a Experimentos
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <>
                {/* Active Experiments */}
                {activeExperiments.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 flex items-center"><Icon source={<ClockIcon />} color="currentColor" /></span>
                      <Title as="h3">Experimentos Activos</Title>
                    </div>
                    <div className="space-y-3">
                      {activeExperiments.map((exp) => (
                        <ExperimentCard
                          key={exp.id}
                          experiment={exp}
                          onClick={() => navigate(`/dashboard/experiments/${exp.id}/results`)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Completed Experiments */}
                {completedExperiments.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 flex items-center"><Icon source={<CheckCircleIcon />} color="currentColor" /></span>
                      <Title as="h3">Experimentos Completados</Title>
                    </div>
                    <div className="space-y-3">
                      {completedExperiments.map((exp) => (
                        <ExperimentCard
                          key={exp.id}
                          experiment={exp}
                          onClick={() => navigate(`/dashboard/experiments/${exp.id}/results`)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Experiments (Paused, Draft) */}
                {otherExperiments.length > 0 && (
                  <div className="space-y-4">
                    <Title as="h3">Otros Experimentos</Title>
                    <div className="space-y-3">
                      {otherExperiments.map((exp) => (
                        <ExperimentCard
                          key={exp.id}
                          experiment={exp}
                          onClick={() => navigate(`/dashboard/experiments/${exp.id}/results`)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Archived Experiments */}
                {archivedExperiments.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <ArchiveIcon size={20} />
                      <Title as="h3">Archivados</Title>
                    </div>
                    <div className="space-y-3">
                      {archivedExperiments.map((exp) => (
                        <ExperimentCard
                          key={exp.id}
                          experiment={exp}
                          onClick={() => navigate(`/dashboard/experiments/${exp.id}/results`)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Tabs.Item>
      </Tabs>
    </div>
  )
}

// Experiment Card Component for the list view
interface ExperimentCardProps {
  experiment: Experiment
  onClick: () => void
}

function ExperimentCard({ experiment, onClick }: ExperimentCardProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <div className="shrink-0 whitespace-nowrap">
            <Tag appearance="primary">
              <Icon source={<ClockIcon />} color="currentColor" />
              Activo
            </Tag>
          </div>
        )
      case 'completed':
        return (
          <div className="shrink-0 whitespace-nowrap">
            <Tag appearance="success">
              <Icon source={<CheckCircleIcon />} color="currentColor" />
              Completado
            </Tag>
          </div>
        )
      case 'paused':
        return (
          <div className="shrink-0 whitespace-nowrap">
            <Tag appearance="warning">
              <Icon source={<StopIcon />} color="currentColor" />
              Pausado
            </Tag>
          </div>
        )
      case 'archived':
        return (
          <div className="shrink-0 whitespace-nowrap">
            <Tag appearance="neutral">
              <Icon source={<ArchiveIcon />} color="currentColor" />
              Archivado
            </Tag>
          </div>
        )
      default:
        return (
          <div className="shrink-0 whitespace-nowrap">
            <Tag appearance="neutral">
              <Icon source={<ExclamationCircleIcon />} color="currentColor" />
              Borrador
            </Tag>
          </div>
        )
    }
  }

  return (
    <Card
      className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-sm"
      onClick={onClick}
    >
      <Card.Body>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <Title as="h4" className="truncate">{experiment.name}</Title>
              {getStatusBadge(experiment.status)}
            </div>
            <Text className="truncate">
              {experiment.hypothesis || 'Sin hipótesis definida'}
            </Text>
          </div>

          <div className="flex items-center ml-4">
            <Icon source={<ArrowRightIcon />} />
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
